// Accessibility helpers: live-region announcer, roving-tabindex grid navigation, and a
// visually-hidden DOM-model helper. All CSP-safe (class/attribute driven).

/** Create (once) and return a polite live region; announce text into it. */
let _live: HTMLElement | null = null;
export function announce(message: string, assertive = false): void {
  if (!_live) {
    _live = document.createElement('div');
    _live.className = 'visually-hidden';
    _live.setAttribute('role', 'status');
    _live.setAttribute('aria-live', 'polite');
    _live.setAttribute('aria-atomic', 'true');
    document.body.appendChild(_live);
  }
  _live.setAttribute('aria-live', assertive ? 'assertive' : 'polite');
  // Clear then set on next frame so repeated identical messages re-announce.
  _live.textContent = '';
  requestAnimationFrame(() => {
    if (_live) _live.textContent = message;
  });
}

/**
 * Roving-tabindex keyboard navigation over a set of cells laid out in a grid.
 * Arrow keys move focus; Home/End jump; only the active cell is tabbable.
 * Returns a teardown function.
 */
export function rovingGrid(
  cells: HTMLElement[],
  columns: number,
  onActivate?: (index: number) => void,
): () => void {
  let active = 0;
  const sync = () => {
    cells.forEach((c, i) => {
      c.tabIndex = i === active ? 0 : -1;
    });
  };
  const focus = (i: number) => {
    active = Math.max(0, Math.min(cells.length - 1, i));
    sync();
    cells[active].focus();
  };
  const onKey = (e: KeyboardEvent) => {
    const i = cells.indexOf(e.target as HTMLElement);
    if (i < 0) return;
    let next = i;
    switch (e.key) {
      case 'ArrowRight': next = i + 1; break;
      case 'ArrowLeft': next = i - 1; break;
      case 'ArrowDown': next = i + columns; break;
      case 'ArrowUp': next = i - columns; break;
      case 'Home': next = 0; break;
      case 'End': next = cells.length - 1; break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        onActivate?.(i);
        return;
      default: return;
    }
    if (next >= 0 && next < cells.length) {
      e.preventDefault();
      focus(next);
    }
  };
  cells.forEach((c) => c.addEventListener('keydown', onKey));
  sync();
  return () => cells.forEach((c) => c.removeEventListener('keydown', onKey));
}

/** Trap focus within a container (for modals/onboarding sheets). Returns teardown. */
export function trapFocus(container: HTMLElement): () => void {
  const selector =
    'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])';
  const onKey = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const items = Array.from(container.querySelectorAll<HTMLElement>(selector)).filter(
      (el) => el.offsetParent !== null,
    );
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };
  container.addEventListener('keydown', onKey);
  return () => container.removeEventListener('keydown', onKey);
}
